import { GRAPHHOPPER_API_KEY } from '../config/constants';
import { calculateDistance, getSurfaceColor } from './mapHelpers';

/**
 * Calcule l'itinéraire avec GraphHopper ou OSRM en fallback
 */
export const calculateRouteGeometry = async (pointsList, transportMode) => {
    const coordinates = pointsList.map(p => `${p[1]},${p[0]}`).join(';');

    // Essayer GraphHopper en premier
    if (GRAPHHOPPER_API_KEY && GRAPHHOPPER_API_KEY.trim() !== '') {
        try {
            const pts = pointsList.map(p => `point=${p[0]},${p[1]}`).join('&');
            const ghProfile = transportMode === 'car' ? 'car' : transportMode === 'foot' ? 'foot' : 'bike';
            const ghResponse = await fetch(
                `https://graphhopper.com/api/1/route?${pts}&vehicle=${ghProfile}&key=${GRAPHHOPPER_API_KEY}&points_encoded=false`
            );

            if (ghResponse.ok) {
                const ghData = await ghResponse.json();
                if (ghData.paths && ghData.paths[0]) {
                    return ghData.paths[0].points.coordinates.map(c => [c[1], c[0]]);
                }
            }
        } catch (ghErr) {
            console.warn('Erreur GraphHopper:', ghErr);
        }
    }

    // Fallback sur OSRM
    try {
        const osrmProfile = transportMode === 'car' ? 'car' : transportMode === 'foot' ? 'foot' : 'bike';
        const response = await fetch(
            `https://router.project-osrm.org/route/v1/${osrmProfile}/${coordinates}?overview=full&geometries=geojson`
        );

        if (!response.ok) return null;

        const data = await response.json();
        if (data.routes && data.routes.length > 0) {
            return data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
        }
    } catch (err) {
        console.error('Erreur calcul itinéraire:', err);
    }

    return null;
};

/**
 * Analyse l'itinéraire standard avec GraphHopper
 */
export const analyzeStandardRoute = async (points, routeGeometry, transportMode) => {
    const routeToAnalyze = routeGeometry.length > 0 ? routeGeometry : points;
    const totalDistance = calculateDistance(routeToAnalyze);

    const pointsQuery = points.map(p => `point=${p[0]},${p[1]}`).join('&');
    const ghProfile = transportMode === 'car' ? 'car' : transportMode === 'foot' ? 'foot' : 'bike';

    const response = await fetch(
        `https://graphhopper.com/api/1/route?${pointsQuery}&vehicle=${ghProfile}&key=${GRAPHHOPPER_API_KEY}&points_encoded=false&details=road_class&details=surface&details=max_speed`
    );

    if (!response.ok) {
        throw new Error('Erreur lors de la requête GraphHopper');
    }

    const data = await response.json();

    if (!data.paths || data.paths.length === 0) {
        throw new Error('Aucun itinéraire trouvé');
    }

    const path = data.paths[0];
    const geometry = path.points.coordinates.map(c => [c[1], c[0]]);

    const surfaceData = {};
    const roadClassData = {};
    let totalSegments = 0;
    const segments = [];

    // Analyser les surfaces
    if (path.details && path.details.surface) {
        path.details.surface.forEach(seg => {
            const surface = seg[2];
            const len = seg[1] - seg[0];
            const sg = geometry.slice(seg[0], seg[1] + 1);

            if (sg.length >= 2) {
                segments.push({
                    geometry: sg,
                    surface,
                    color: getSurfaceColor(surface)
                });
            }

            if (!surfaceData[surface]) surfaceData[surface] = 0;
            surfaceData[surface] += len;
            totalSegments += len;
        });
    }

    // Analyser les classes de route
    if (path.details && path.details.road_class) {
        path.details.road_class.forEach(seg => {
            const rc = seg[2];
            const len = seg[1] - seg[0];
            if (!roadClassData[rc]) roadClassData[rc] = 0;
            roadClassData[rc] += len;
        });
    }

    // Calculer les pourcentages de surface
    const surfaceResults = {};
    Object.keys(surfaceData).forEach(s => {
        surfaceResults[s] = {
            count: surfaceData[s],
            percentage: ((surfaceData[s] / totalSegments) * 100).toFixed(1),
            inferred: false,
            confidence: 'verified'
        };
    });

    // Calculer les pourcentages de classe de route
    const roadClassResults = {};
    Object.keys(roadClassData).forEach(rc => {
        roadClassResults[rc] = ((roadClassData[rc] / totalSegments) * 100).toFixed(1);
    });

    return {
        geometry,
        segments,
        data: {
            distance: totalDistance.toFixed(2),
            duration: Math.round(path.time / 1000 / 60), // Durée en minutes
            surfaces: Object.keys(surfaceResults).length > 0 ? surfaceResults : {
                'unknown': { count: 1, percentage: '100.0', inferred: false, confidence: 'none' }
            },
            roadClasses: roadClassResults,
            totalSegments,
            explicitSurfaceCount: Object.keys(surfaceResults).length > 0 ? totalSegments : 0
        }
    };
};

/**
 * Analyse l'itinéraire alternatif avec BRouter puis GraphHopper
 */
export const analyzeAlternativeRoute = async (points, transportMode, surfacePreferences) => {
    const lonlats = points.map(p => `${p[1]},${p[0]}`).join('|');

    // Déterminer le profil BRouter
    let profile = 'gravel';
    if (transportMode === 'car') {
        profile = 'car-fast';
    } else if (transportMode === 'foot') {
        profile = 'hiking-mountain';
    } else {
        const offTerrainTotal = surfacePreferences.gravel + surfacePreferences.dirt + surfacePreferences.grass;
        profile = offTerrainTotal > 50 ? 'gravel' : 'safety';
    }

    try {
        // Essayer BRouter
        const response = await fetch(
            `https://brouter.de/brouter?lonlats=${lonlats}&profile=${profile}&alternativeidx=0&format=geojson`
        );

        if (response.ok) {
            const data = await response.json();

            if (data.features && data.features.length > 0) {
                const feature = data.features[0];
                const geometry = feature.geometry.coordinates.map(c => [c[1], c[0]]);
                const distance = (feature.properties['track-length'] / 1000).toFixed(2);

                return await analyzeBrouterRouteWithGraphHopper(geometry, distance, transportMode);
            }
        }
    } catch (err) {
        console.warn('Erreur BRouter:', err);
    }

    // Fallback sur GraphHopper avec custom model
    return await analyzeAlternativeRouteGraphHopper(points, transportMode, surfacePreferences);
};

/**
 * Analyse une route BRouter avec les détails GraphHopper
 */
const analyzeBrouterRouteWithGraphHopper = async (geometry, distance, transportMode) => {
    const fallback = {
        geometry,
        segments: [],
        data: {
            distance,
            duration: null,
            surfaces: { 'unknown': { count: 1, percentage: '100.0', inferred: false, confidence: 'none' } },
            roadClasses: {},
            totalSegments: 0,
            explicitSurfaceCount: 0,
            source: 'brouter'
        }
    };

    if (!GRAPHHOPPER_API_KEY || GRAPHHOPPER_API_KEY.trim() === '') {
        return fallback;
    }

    // Simplifier la géométrie pour GraphHopper (max 5 points)
    const maxPoints = 5;
    const simplifiedPoints = [];

    if (geometry.length <= maxPoints) {
        simplifiedPoints.push(...geometry);
    } else {
        simplifiedPoints.push(geometry[0]);
        const step = (geometry.length - 1) / (maxPoints - 1);
        for (let i = 1; i < maxPoints - 1; i++) {
            simplifiedPoints.push(geometry[Math.round(i * step)]);
        }
        simplifiedPoints.push(geometry[geometry.length - 1]);
    }

    const ghProfile = transportMode === 'car' ? 'car' : transportMode === 'foot' ? 'foot' : 'bike';
    const pointsQuery = simplifiedPoints.map(p => `point=${p[0]},${p[1]}`).join('&');

    try {
        const response = await fetch(
            `https://graphhopper.com/api/1/route?${pointsQuery}&vehicle=${ghProfile}&key=${GRAPHHOPPER_API_KEY}&points_encoded=false&details=road_class&details=surface`
        );

        if (!response.ok) return fallback;

        const data = await response.json();

        if (!data.paths || data.paths.length === 0) return fallback;

        const path = data.paths[0];
        const surfaceData = {};
        const roadClassData = {};
        let totalSegments = 0;
        const segments = [];

        // Analyser les surfaces
        if (path.details && path.details.surface) {
            path.details.surface.forEach(seg => {
                const surface = seg[2];
                const len = seg[1] - seg[0];

                // Mapper les indices sur la géométrie originale
                const ratio = geometry.length / (path.points?.coordinates?.length || simplifiedPoints.length);
                const startIdx = Math.floor(seg[0] * ratio);
                const endIdx = Math.min(Math.ceil(seg[1] * ratio), geometry.length - 1);
                const segGeometry = geometry.slice(startIdx, endIdx + 1);

                if (segGeometry.length >= 2) {
                    segments.push({
                        geometry: segGeometry,
                        surface,
                        color: getSurfaceColor(surface)
                    });
                }

                if (!surfaceData[surface]) surfaceData[surface] = 0;
                surfaceData[surface] += len;
                totalSegments += len;
            });
        }

        // Analyser les classes de route
        if (path.details && path.details.road_class) {
            path.details.road_class.forEach(seg => {
                const rc = seg[2];
                const len = seg[1] - seg[0];
                if (!roadClassData[rc]) roadClassData[rc] = 0;
                roadClassData[rc] += len;
            });
        }

        const surfaceResults = {};
        Object.keys(surfaceData).forEach(s => {
            surfaceResults[s] = {
                count: surfaceData[s],
                percentage: ((surfaceData[s] / totalSegments) * 100).toFixed(1),
                inferred: false,
                confidence: 'verified'
            };
        });

        const roadClassResults = {};
        Object.keys(roadClassData).forEach(rc => {
            roadClassResults[rc] = ((roadClassData[rc] / totalSegments) * 100).toFixed(1);
        });

        return {
            geometry,
            segments,
            data: {
                distance,
                duration: path.time ? Math.round(path.time / 1000 / 60) : null, // Durée en minutes
                surfaces: Object.keys(surfaceResults).length > 0 ? surfaceResults : {
                    'unknown': { count: 1, percentage: '100.0', inferred: false, confidence: 'none' }
                },
                roadClasses: roadClassResults,
                totalSegments,
                explicitSurfaceCount: Object.keys(surfaceResults).length > 0 ? totalSegments : 0,
                source: 'brouter'
            }
        };
    } catch (err) {
        console.error('Erreur analyse BRouter avec GraphHopper:', err);
        return fallback;
    }
};

/**
 * Analyse l'itinéraire alternatif avec GraphHopper et custom model
 */
const analyzeAlternativeRouteGraphHopper = async (points, transportMode, surfacePreferences) => {
    const ghProfile = transportMode === 'car' ? 'car' : transportMode === 'foot' ? 'foot' : 'bike';

    // Calculer les poids pour le custom model
    const asphaltWeight = Math.max(0.1, surfacePreferences.asphalt / 50);
    const gravelWeight = Math.max(1.0, surfacePreferences.gravel / 10);
    const dirtWeight = Math.max(1.0, surfacePreferences.dirt / 10);
    const grassWeight = Math.max(1.0, surfacePreferences.grass / 10);
    const pavedWeight = Math.max(0.1, surfacePreferences.paved / 50);

    const customModel = {
        "priority": [
            { "if": "surface == ASPHALT", "multiply_by": asphaltWeight },
            { "if": "surface == GRAVEL", "multiply_by": gravelWeight },
            { "if": "surface == DIRT || surface == GROUND", "multiply_by": dirtWeight },
            { "if": "surface == GRASS", "multiply_by": grassWeight },
            { "if": "surface == PAVED || surface == PAVING_STONES", "multiply_by": pavedWeight },
            { "if": "road_class == TRACK || road_class == PATH", "multiply_by": Math.max(gravelWeight, dirtWeight) },
            { "if": "road_class == PRIMARY || road_class == SECONDARY", "multiply_by": asphaltWeight }
        ],
        "distance_influence": 100
    };

    const pointsQuery = points.map(p => `point=${p[0]},${p[1]}`).join('&');

    const response = await fetch(
        `https://graphhopper.com/api/1/route?${pointsQuery}&profile=${ghProfile}&key=${GRAPHHOPPER_API_KEY}&points_encoded=false&details=road_class&details=surface&ch.disable=true&custom_model=${encodeURIComponent(JSON.stringify(customModel))}`
    );

    if (!response.ok) {
        throw new Error('Erreur GraphHopper alternative route');
    }

    const data = await response.json();

    if (!data.paths || data.paths.length === 0) {
        throw new Error('Aucun itinéraire alternatif trouvé');
    }

    const path = data.paths[0];
    const geometry = path.points.coordinates.map(c => [c[1], c[0]]);
    const totalDistance = calculateDistance(geometry);

    const surfaceData = {};
    const roadClassData = {};
    let totalSegments = 0;
    const segments = [];

    // Analyser les surfaces
    if (path.details && path.details.surface) {
        path.details.surface.forEach(seg => {
            const surface = seg[2];
            const len = seg[1] - seg[0];
            const segGeometry = geometry.slice(seg[0], seg[1] + 1);

            if (segGeometry.length >= 2) {
                segments.push({
                    geometry: segGeometry,
                    surface,
                    color: getSurfaceColor(surface)
                });
            }

            if (!surfaceData[surface]) surfaceData[surface] = 0;
            surfaceData[surface] += len;
            totalSegments += len;
        });
    }

    // Analyser les classes de route
    if (path.details && path.details.road_class) {
        path.details.road_class.forEach(seg => {
            const rc = seg[2];
            const len = seg[1] - seg[0];
            if (!roadClassData[rc]) roadClassData[rc] = 0;
            roadClassData[rc] += len;
        });
    }

    const surfaceResults = {};
    Object.keys(surfaceData).forEach(s => {
        surfaceResults[s] = {
            count: surfaceData[s],
            percentage: ((surfaceData[s] / totalSegments) * 100).toFixed(1),
            inferred: false,
            confidence: 'verified'
        };
    });

    const roadClassResults = {};
    Object.keys(roadClassData).forEach(rc => {
        roadClassResults[rc] = ((roadClassData[rc] / totalSegments) * 100).toFixed(1);
    });

    return {
        geometry,
        segments,
        data: {
            distance: totalDistance.toFixed(2),
            duration: Math.round(path.time / 1000 / 60), // Durée en minutes
            surfaces: Object.keys(surfaceResults).length > 0 ? surfaceResults : {
                'unknown': { count: 1, percentage: '100.0', inferred: false, confidence: 'none' }
            },
            roadClasses: roadClassResults,
            totalSegments,
            explicitSurfaceCount: Object.keys(surfaceResults).length > 0 ? totalSegments : 0,
            source: 'graphhopper'
        }
    };
};